import { useConnections } from '@membranehq/react';
import { ChatCircle, Envelope, Phone } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'src/components/Card';
import {
  AutoCompleteMultiSelectBox,
  Option,
} from 'src/components/AutoCompleteMultiSelectBox';
import { Label } from 'src/components/Label';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Number, NumberTags } from 'src/interfaces/number.interface';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import { processPhoneNumber } from 'src/utils/number';
import { buildEncodedTagOptions } from 'src/components/tag/tag.helpers';
import { useWizard } from '../../../components/wizard/WizardContext';
import { EmailProviderSelect } from './EmailProviderSelect';
import { SmsProviderSelect } from './SmsProviderSelect';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Link } from 'react-router-dom';
interface OutboundConfigurationsProps {
  className?: string;
}

export const OutboundConfigurations = ({
  className = '',
}: OutboundConfigurationsProps) => {
  const { campaignAutomation, setCampaignAutomation } = useWizard();

  const { get } = useAuthedRequest();
  const { connections: integrationAppConnections } = useConnections();

  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    campaignAutomation.fromNumberOptions,
  );
  const [options, setOptions] = useState<Option[]>([]);
  const [hasNumbers, setHasNumbers] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const searchNumbersAndTags = useCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const trimmedQuery = query.trim();

        // Search numbers and tags in parallel
        const numberQueryObject: QueryObject = {
          page: '1',
          size: '10',
          filters: JSON.stringify({
            number: trimmedQuery.length !== 0 ? trimmedQuery : undefined,
          }),
        };
        const numberParams = new URLSearchParams(
          numberQueryObject as Record<string, string>,
        );

        const tagQueryObject: QueryObject = {
          page: '1',
          size: '10',
        };
        if (trimmedQuery) {
          tagQueryObject.filters = JSON.stringify({
            startsWith: trimmedQuery,
          });
        }
        const tagParams = new URLSearchParams(
          tagQueryObject as Record<string, string>,
        );

        const [numbersData, tagsData] = await Promise.all([
          get(`/numbers?${numberParams.toString()}`),
          get(`/numbers/tags?${tagParams.toString()}`),
        ]);

        const numberOptions: Option[] = (numbersData.items || []).map(
          (n: Number) => ({
            value: n.number,
            label: processPhoneNumber(n.number),
          }),
        );

        const tagOptions = buildEncodedTagOptions(
          (tagsData as NumberTags).items || [],
        );

        setOptions([...tagOptions, ...numberOptions]);

        // Only check for numbers on initial load (empty query)
        if (trimmedQuery === '' && hasNumbers === null) {
          setHasNumbers(numberOptions.length > 0);
        }
      } catch (error) {
        console.error('Failed to search numbers and tags:', error);
      } finally {
        setLoading(false);
      }
    },
    [get, hasNumbers],
  );

  useEffect(() => {
    searchNumbersAndTags('');
  }, [searchNumbersAndTags]);

  const hasOutreachByType = (type: CommunicationChannel) => {
    return (
      campaignAutomation.followUpCommunications.length > 0 &&
      campaignAutomation.followUpCommunications.some(
        (c) => c.contactVia === type,
      )
    );
  };

  const isValidEmailConfig = useMemo(() => {
    const emailFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.EMAIL,
    );

    if (emailFollowUps.length === 0) return true;

    // Check if campaign-level email provider is set and authenticated
    if (!campaignAutomation.emailProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key ===
          campaignAutomation.emailProviderOption?.value && !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.emailProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  const isValidSmsConfig = useMemo(() => {
    const smsFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.SMS,
    );

    if (smsFollowUps.length === 0) return true;

    // Check if campaign-level SMS provider is set and authenticated
    if (!campaignAutomation.smsProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === campaignAutomation.smsProviderOption?.value &&
        !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.smsProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-700 mb-2">
          Outbound Settings
        </h2>
        <p className="text-neutral-600">
          Set the sender details for calls, emails, and messages.
        </p>
      </div>

      {/* Phone Calls Section */}
      <Card className="p-6 bg-neutral-50 border-neutral-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Phone size={20} className="text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-700">Calls</h3>
        </div>

        <div className="space-y-4">
          {/* Select from numbers */}
          <div>
            <Label required htmlFor="from-numbers">
              Calls will come from
            </Label>
            {hasNumbers !== false && (
              <AutoCompleteMultiSelectBox
                fetchOptions={searchNumbersAndTags}
                selectedOptions={selectedOptions}
                onSelectionChange={(options) => {
                  setSelectedOptions(options);
                  setCampaignAutomation({
                    ...campaignAutomation,
                    fromNumberOptions: [...options],
                  });
                }}
                options={options}
                placeholder="Select caller ID phone numbers or tags..."
                disabled={loading}
                variant="outlined"
                color="primary"
                size="medium"
                className="mt-2"
              />
            )}

            {hasNumbers === false && (
              <p className="text-sm text-neutral-500">
                No numbers found. Please add a number to your account.{' '}
                <Link to="/numbers" className="text-primary-500">
                  Add a number
                </Link>
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Email Section */}
      {hasOutreachByType(CommunicationChannel.EMAIL) && (
        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Envelope size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-700">Emails</h3>
          </div>

          <EmailProviderSelect
            onChange={(provider) => {
              setCampaignAutomation({
                ...campaignAutomation,
                emailProviderOption: provider,
              });
            }}
            isAuthenticated={isValidEmailConfig}
          />

          <p className="text-sm text-warning-500 mt-1">
            Warning: Sending large volumes of emails may result in your domain
            being flagged as spam.
          </p>
        </Card>
      )}

      {/* SMS Messages Section */}
      {hasOutreachByType(CommunicationChannel.SMS) && (
        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ChatCircle size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-700">SMS</h3>
          </div>

          <SmsProviderSelect
            onChange={(provider) => {
              setCampaignAutomation({
                ...campaignAutomation,
                smsProviderOption: provider,
              });
            }}
            isAuthenticated={isValidSmsConfig}
          />

          <p className="text-sm text-warning-500 mt-1">
            Warning: Sending large volumes of SMS messages may result in your
            numbers being flagged as spam.
          </p>
        </Card>
      )}
    </div>
  );
};
